/**
 * Hook for redeeming received Cashu ecash tokens.
 *
 * Manages the receive lifecycle: decoding a token string, calling
 * wallet.receive() to swap proofs at the mint, storing new proofs
 * in Evolu, and creating a transaction record.
 *
 * SECURITY: Never logs token strings or proof secrets.
 *
 * Tier: 1 (beginner) -- core receive functionality.
 *
 * @module cashu/hooks/useCashuReceive
 */

import { useState, useCallback } from 'react';
import { getDecodedToken } from '@cashu/cashu-ts';
import type { Wallet } from '@cashu/cashu-ts';
import { proofsToEvolu } from '@/features/cashu/utils/proof-management';
import type { EvoluTokenRow, CashuProof } from '@/features/cashu/utils/proof-management';

/** Receive state machine states. */
export type CashuReceiveState =
  | 'idle'
  | 'decoding'
  | 'receiving'
  | 'storing'
  | 'complete'
  | 'error';

/** Factory for getting a wallet instance for a given mint URL. */
export interface WalletFactory {
  /** Get a ready wallet for the mint URL, or null if unavailable. */
  readonly getWallet: (mintUrl: string) => Promise<Wallet | null>;
}

/** Callbacks for persisting receive results to Evolu. */
export interface CashuReceivePersistence {
  /** Store received proofs as new active tokens. */
  readonly storeReceivedProofs: (proofs: EvoluTokenRow[]) => void;
  /** Insert a transaction record. */
  readonly insertTransaction: (tx: ReceiveTransactionRecord) => void;
  /** Look up the mint ID for a given mint URL, or null if unknown. */
  readonly getMintIdForUrl: (mintUrl: string) => string | null;
}

/** Transaction record for receive operations. */
export interface ReceiveTransactionRecord {
  readonly type: 'cashu';
  readonly direction: 'incoming';
  readonly custodyType: 'cashu-mint';
  readonly amountSats: number;
  readonly settledAt: string;
  readonly mintId: string | null;
  readonly note: string | null;
  readonly kind: 'receive';
}

/** Result from a successful receive. */
export interface CashuReceiveResult {
  /** Amount received in satoshis. */
  readonly amount: number;
  /** Mint URL the token was from. */
  readonly mintUrl: string;
  /** Number of proofs received. */
  readonly proofCount: number;
}

/** Return type for the useCashuReceive hook. */
export interface UseCashuReceiveResult {
  /** Current state of the receive operation. */
  readonly state: CashuReceiveState;
  /** Error from the last receive attempt, or null. */
  readonly error: string | null;
  /** Result of the last successful receive, or null. */
  readonly result: CashuReceiveResult | null;
  /** Execute a receive operation with a token string. */
  readonly receive: (tokenString: string, note?: string) => Promise<CashuReceiveResult | null>;
  /** Reset state back to idle. */
  readonly reset: () => void;
}

/**
 * Hook for redeeming received Cashu ecash tokens.
 *
 * Decodes the token string, gets a wallet for the mint, calls
 * wallet.receive() to swap proofs, stores results in Evolu.
 *
 * @param walletFactory - Factory for creating wallet instances per mint
 * @param persistence - Callbacks for Evolu persistence
 * @returns Object with receive state, receive function, and result
 */
export function useCashuReceive(
  walletFactory: WalletFactory,
  persistence: CashuReceivePersistence,
): UseCashuReceiveResult {
  const [state, setState] = useState<CashuReceiveState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CashuReceiveResult | null>(null);

  const reset = useCallback((): void => {
    setState('idle');
    setError(null);
    setResult(null);
  }, []);

  const receive = useCallback(
    async (tokenString: string, note?: string): Promise<CashuReceiveResult | null> => {
      setState('decoding');
      setError(null);

      try {
        // Validate and decode token
        const trimmed = tokenString.trim();
        if (!trimmed.startsWith('cashuA') && !trimmed.startsWith('cashuB')) {
          setState('error');
          setError("This doesn't look like a valid Cashu token. Check the text and try again.");
          return null;
        }

        const decoded = getDecodedToken(trimmed);
        const mintUrl = decoded.mint;

        if (!mintUrl) {
          setState('error');
          setError('Token is missing mint information.');
          return null;
        }

        // Get wallet for this mint
        setState('receiving');
        const wallet = await walletFactory.getWallet(mintUrl);

        if (!wallet) {
          setState('error');
          setError("Can't reach the mint right now. Try again when you're online.");
          return null;
        }

        // Swap proofs at the mint
        const receivedProofs = await wallet.receive(decoded);

        // Calculate amount
        const amount = (receivedProofs as CashuProof[]).reduce((sum, p) => sum + p.amount, 0);

        // Store proofs in Evolu
        setState('storing');
        const mintId = persistence.getMintIdForUrl(mintUrl);
        const evoluRows = proofsToEvolu(receivedProofs as CashuProof[], mintId ?? mintUrl);
        persistence.storeReceivedProofs(evoluRows);

        // Insert transaction record
        persistence.insertTransaction({
          type: 'cashu',
          direction: 'incoming',
          custodyType: 'cashu-mint',
          amountSats: amount,
          settledAt: new Date().toISOString(),
          mintId,
          note: note ?? null,
          kind: 'receive',
        });

        const receiveResult: CashuReceiveResult = {
          amount,
          mintUrl,
          proofCount: receivedProofs.length,
        };

        setState('complete');
        setResult(receiveResult);

        return receiveResult;
      } catch (err) {
        setState('error');
        const message = categorizeReceiveError(err);
        setError(message);
        return null;
      }
    },
    [walletFactory, persistence],
  );

  return { state, error, result, receive, reset };
}

/**
 * Categorize receive errors into user-friendly messages.
 *
 * @param err - The error from the receive operation
 * @returns User-friendly error message
 */
function categorizeReceiveError(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err);
  const lower = message.toLowerCase();

  if (lower.includes('spent') || lower.includes('already')) {
    return 'This token has already been redeemed. No funds were added.';
  }

  if (lower.includes('network') || lower.includes('fetch') || lower.includes('timeout')) {
    return "Can't reach the mint right now. Try again when you're online.";
  }

  if (lower.includes('token') || lower.includes('decode') || lower.includes('invalid')) {
    return "This doesn't look like a valid Cashu token. Check the text and try again.";
  }

  return `Receive failed: ${message}`;
}
